import type { DayEvent } from '../stores/events'
import type { Gender } from './types'
import { centile, corridor } from './who-weight'

/**
 * Вес малыша: точки взвешиваний и что о них сказать.
 *
 * Решения заказчика (27–28.09.2026): шкала — коридор ВОЗ «вес к возрасту»; три состояния
 * плюс «давно не взвешивали» и «пока только вес при рождении»; никаких диагнозов и
 * «обратитесь к педиатру» — только факт относительно нормы. Домашние весы врут на
 * ±100 г, поэтому тренд считается по записям не чаще раза в неделю.
 */

const DAY = 24 * 3600 * 1000

/** Запись старше этого — «давно не взвешивали». */
export const STALE_AFTER_DAYS = 28

export interface WeightPoint {
  at: number
  grams: number
  /** Вес при рождении: из анкеты, а не из журнала. */
  birth?: boolean
}

export type WeightState = 'ok' | 'low' | 'high' | 'stale' | 'empty'

/**
 * Почему состояние такое. Без этого карточка говорила обтекаемо — «ниже коридора, или
 * рост замедлился», — хотя знает, что именно сработало (замечание 04.10.2026).
 */
export type WeightReason = 'below' | 'above' | 'slow' | 'drop'

export interface WeightSummary {
  state: WeightState
  /** Что именно сработало; у спокойных состояний нет. */
  reason?: WeightReason
  /** Последняя точка, если есть. */
  last: WeightPoint | null
  /** Центиль последней точки, 0–100. */
  centile: number | null
  /** Прибавка к предыдущей точке: граммы и дни между ними. */
  delta: { grams: number; days: number } | null
  /**
   * Сколько надо было прибавить, чтобы не выйти за нижнюю границу коридора: разница
   * между границей на день взвешивания и прошлым весом. Родители считают прибавкой,
   * а не центилями, и мерить её просили по нижней границе, а не по середине: середина
   * в 6–7 кг к трём месяцам пугает (просьба 04.10.2026).
   */
  floorGain: number | null
}

/** Все точки по порядку: вес при рождении из анкеты и записи журнала. */
export function weightPoints(events: DayEvent[], birthMillis: number, birthWeightGrams?: number): WeightPoint[] {
  const points: WeightPoint[] = []
  if (birthWeightGrams !== undefined && birthWeightGrams > 0) points.push({ at: birthMillis, grams: birthWeightGrams, birth: true })
  for (const e of events) {
    if (e.kind === 'weight' && e.deleted !== true && typeof e.grams === 'number' && e.grams > 0) points.push({ at: e.at, grams: e.grams })
  }
  return points.sort((a, b) => a.at - b.at)
}

/** Границы коридоров ВОЗ: 3, 15, 50, 85, 97 — переход через две вниз и есть «внимание». */
const КОРИДОРЫ = [3, 15, 50, 85, 97]
const коридорНомер = (c: number): number => КОРИДОРЫ.filter((граница) => c >= граница).length

/** Прибавка к предыдущей точке — той, что не ближе недели: чаще весы дома врут сильнее прибавки. */
export function lastDelta(points: WeightPoint[]): { grams: number; days: number } | null {
  const last = points[points.length - 1]
  if (last === undefined) return null
  const prev = [...points].reverse().find((p) => p !== last && last.at - p.at >= 7 * DAY) ?? points[points.length - 2]
  if (prev === undefined) return null
  return { grams: last.grams - prev.grams, days: Math.max(1, Math.round((last.at - prev.at) / DAY)) }
}

/** Минимальная прибавка от веса [fromGrams] в момент [from], чтобы в момент [to] не оказаться ниже 3-го центиля. */
export function floorGain(gender: Gender, birthMillis: number, from: number, to: number, fromGrams: number): number {
  void from
  return Math.max(0, corridor(gender, birthMillis, to).low - fromGrams)
}

export function weightSummary(points: WeightPoint[], gender: Gender, birthMillis: number, now: number): WeightSummary {
  const взвешивания = points.filter((p) => p.birth !== true)
  const last = points[points.length - 1] ?? null
  if (last === null || взвешивания.length === 0) return { state: 'empty', last, centile: null, delta: null, floorGain: null }

  const c = centile(last.grams, gender, birthMillis, last.at)
  const delta = lastDelta(points)
  const минимум = delta === null ? null : Math.round(floorGain(gender, birthMillis, last.at - delta.days * DAY, last.at, last.grams - delta.grams))
  const общее = { last, centile: c, delta, floorGain: минимум }

  if (now - last.at > STALE_AFTER_DAYS * DAY) return { state: 'stale', ...общее }

  // Первые две недели: физиологическая убыль больше десятой части веса при рождении
  const birth = points.find((p) => p.birth === true)
  if (birth !== undefined && last.at - birthMillis <= 14 * DAY && last.grams < birth.grams * 0.9) {
    return { state: 'low', reason: 'drop', ...общее }
  }

  if (c < 3) return { state: 'low', reason: 'below', ...общее }
  if (c > 97) return { state: 'high', reason: 'above', ...общее }

  // Съехал на два коридора вниз между двумя взвешиваниями с разницей не меньше двух недель
  const prev = [...points].reverse().find((p) => p !== last && last.at - p.at >= 14 * DAY)
  if (prev !== undefined) {
    const было = коридорНомер(centile(prev.grams, gender, birthMillis, prev.at))
    if (было - коридорНомер(c) >= 2) return { state: 'low', reason: 'slow', ...общее }
  }
  return { state: 'ok', ...общее }
}

/** Точки коридора для графика: от рождения до горизонта, шаг в неделю. */
export function corridorPath(gender: Gender, birthMillis: number, untilMillis: number): Array<{ at: number; low: number; mid: number; high: number }> {
  const out = []
  for (let at = birthMillis; at <= untilMillis; at += 7 * DAY) out.push({ at, ...corridor(gender, birthMillis, at) })
  return out
}
